/**
 * WeaveIn.ai Platform - Field Configuration Manager
 * Admin UI to manage an organization's field definitions.
 *
 * TRUS-394: this is the "Field Builder" tab. It used to list custom fields only,
 * which left standard (core) fields stranded in a separate "Field Display" tab
 * with a different presentation. It now renders standard and custom fields in one
 * table with an Origin column, so both kinds get the same browsing, filtering and
 * edit affordances. Standard fields are owned by the parent
 * (MemberFieldConfigManager holds core fields + their FieldConfiguration rows), so
 * they arrive as `standardFields` and their edits are delegated back up via
 * `onEditStandardField` / `onToggleStandardVisibility`.
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  ArrowRight,
  ArrowLeft,
  Sliders,
  Calculator,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ApiErrorAlert } from '../common/ApiErrorAlert';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import useCustomFields from '@/hooks/useCustomFields';
import {
  ACROSS_LABELS,
  CALC_TYPE_LABELS,
  UNIT_LABELS,
  describeRule,
} from '@/utils/derivedFields';
import { membersAPI, RECORD_TYPE_SCOPES, recordTypeScopeLabel, normalizeAppliesToScope, CustomFieldCreate, FieldCategory, FieldType, FieldOption, VisibilityRules, VisibilityCondition, VisibilityOperator, RecordTypeScope } from '@/services/api/members';

import { useAppTranslation } from '@/contexts/TranslationContext';

/**
 * TRUS-372: a field's stored `com_fields.category` is free text and has drifted
 * into two forms for the same category — the structured category's `category_key`
 * (snake_case, e.g. "address") on some fields and its `display_name` ("Address")
 * on others. The Edit Field Category <Select> keys its options by `display_name`,
 * so a key-form value matched no option and the field rendered as uncategorized
 * ("Select a category") in the editor even though it grouped correctly elsewhere
 * (the profile matches by category_key). Resolve any stored value to its
 * structured category's canonical `display_name` so the editor reflects the real
 * category and re-saves it in the canonical form, converging the drift.
 * Returns the raw value unchanged when it maps to no known category.
 */
export function resolveCategoryDisplayName(
  raw: string | null | undefined,
  fieldCategories: Pick<FieldCategory, 'category_key' | 'display_name'>[]
): string {
  if (!raw) return '';
  const needle = raw.trim().toLowerCase();
  if (!needle) return '';
  const match = fieldCategories.find(
    (fc) =>
      fc.display_name.toLowerCase() === needle ||
      fc.category_key.toLowerCase() === needle ||
      fc.category_key.toLowerCase() === needle.replace(/\s+/g, '_')
  );
  return match ? match.display_name : raw;
}

const FIELD_TYPES: FieldType[] = ['str', 'long_text', 'int', 'bool', 'date', 'datetime', 'time', 'fixed_date', 'select', 'multi_select', 'checkbox', 'radio', 'toggle'];

// Field type descriptions for user guidance
const FIELD_TYPE_DESCRIPTIONS: Record<string, { label: string; description: string }> = {
  str: { label: 'Text', description: 'Short single-line text (e.g., names)' },
  long_text: { label: 'Long text', description: 'Multi-line paragraph text (e.g., a survivor story or case notes)' },
  int: { label: 'Number', description: 'Whole numbers (e.g., age, count)' },
  bool: { label: 'Yes/No', description: 'True or false checkbox values' },
  date: { label: 'Date', description: 'Calendar date (e.g., 01/15/2024)' },
  datetime: { label: 'Date & Time', description: 'Date with time (e.g., 01/15/2024 2:30 PM)' },
  time: { label: 'Time', description: 'Time of day only (e.g., 2:30 PM)' },
  fixed_date: { label: 'Fixed Date', description: 'Immutable date that cannot be changed after set' },
  select: { label: 'Dropdown', description: 'Select one value from a predefined list' },
  multi_select: { label: 'Multi-Select', description: 'Choose one or more values from a list' },
  checkbox: { label: 'Checkbox Group', description: 'Group of checkboxes from a predefined list' },
  radio: { label: 'Radio Buttons', description: 'Select one option from a visible list of choices' },
  toggle: { label: 'On/Off Toggle', description: 'A switch for enabling or disabling a setting' },
  // Fallback mappings for imported data with non-standard types
  text: { label: 'Text', description: 'Text or string values' },
  string: { label: 'Text', description: 'Text or string values' },
  number: { label: 'Number', description: 'Numeric values' },
  integer: { label: 'Number', description: 'Whole numbers' },
  boolean: { label: 'Yes/No', description: 'True or false values' },
  checkbox: { label: 'Yes/No', description: 'Checkbox (true/false)' },
};

// Helper to get friendly label for any field type
const getFieldTypeLabel = (type: string): string => {
  return FIELD_TYPE_DESCRIPTIONS[type]?.label || type;
};

// Helper to get description for any field type
const getFieldTypeDescription = (type: string): string => {
  return FIELD_TYPE_DESCRIPTIONS[type]?.description || `Field type: ${type}`;
};

/**
 * A standard (core) field flattened by the parent with its per-org
 * FieldConfiguration already applied, so this table can render standard rows the
 * same way it renders custom ones without knowing anything about core-field
 * plumbing.
 */
export interface StandardFieldRow {
  field_key: string;
  /** Configured display name, falling back to the core field's default. */
  display_name: string;
  /** The platform default, so a rename can be shown as "(was: …)". */
  default_display_name: string;
  field_type: string;
  /** Resolved category: configured category name, else the core field_group. */
  category: string | null;
  is_required: boolean;
  is_hidden: boolean;
  /** System fields can't be hidden or renamed — the parent enforces this too. */
  is_system_field: boolean;
}

/** Which kinds of field the table is showing. */
type OriginFilter = 'all' | 'standard' | 'custom';

interface FieldConfigManagerProps {
  onNavigate?: (tab: string) => void;
  /** Record-type scope from the parent MemberFieldConfigManager toggle.
   *  When 'activity_event' is selected, this tab must filter to fields
   *  whose applies_to_record_types includes 'activity_event' — otherwise
   *  the user sees member/contact fields under the wrong scope and the
   *  Somos activity fields appear to be missing. */
  recordType?: 'member' | 'contact' | 'activity_event';
  /** Standard (core) fields to merge into the table. Omitted → custom only,
   *  which is how this component behaves when rendered outside the parent. */
  standardFields?: StandardFieldRow[];
  /** Open the parent's core-field configuration dialog. */
  onEditStandardField?: (fieldKey: string) => void;
  /** Flip a standard field's hidden flag via the parent's optimistic handler. */
  onToggleStandardVisibility?: (fieldKey: string) => void;
  /**
   * Structured categories, needed for bulk assignment: the bulk endpoint
   * resolves by `category_key`, not by display name, and silently no-ops on a
   * key it can't find. Passing the real rows keeps the picker honest.
   */
  assignableCategories?: { category_key: string; display_name: string }[];
  /**
   * Custom-field id → configured category display name, when a
   * FieldConfiguration overrides the field's native `com_fields.category`.
   * The override is what Profile Builder groups by, so the table must show it
   * or a bulk assign looks like it silently failed (cf. TRUS-365).
   */
  customCategoryOverrides?: Record<number, string | undefined>;
  /** Called after a bulk write so the parent can reload its configurations. */
  onBulkApplied?: () => void;
}

export default function FieldConfigManager({
  onNavigate,
  recordType,
  standardFields,
  onEditStandardField,
  onToggleStandardVisibility,
  assignableCategories,
  customCategoryOverrides,
  onBulkApplied,
}: FieldConfigManagerProps) {
  const { t } = useAppTranslation();
  const {
    fields,
    categories,
    fieldCategories,
    loading,
    error,
    errorAction,
    retryable,
    createField,
    updateField,
    deleteField,
    refetch,
    // include_group_children: an admin screen over field DEFINITIONS must list group
    // children too, or a child becomes uneditable here once it joins a group (TRUS-302).
  } = useCustomFields({ record_type: recordType, include_hidden: true, include_group_children: true });

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingField, setEditingField] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  // TRUS-394: the table now holds standard + custom fields, so it needs a way to
  // narrow to one kind, a search (the merged list is long), and the
  // group-by-category browsing that the retired Field Display tab provided.
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all');
  const [search, setSearch] = useState('');
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // TRUS-394 item 6: select several fields and assign them a category in one go.
  // Keyed by BuilderRow.key, which namespaces the two origins' id spaces.
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [bulkCategoryKey, setBulkCategoryKey] = useState<string>('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const [formData, setFormData] = useState<CustomFieldCreate>({
    name: '',
    type: 'str',
    friendly_name: '',
    category: '',
    default_value: '',
    mandatory: false,
    str_len: 128,
    nullable: true,
    hidden: false,
    field_options: [],
    visibility_rules: null,
    applies_to_record_types: null,
    computed_formula: null,
  });

  const [showRulesEditor, setShowRulesEditor] = useState(false);
  // TRUS-388: whether the Calculation section is open (i.e. the field is derived).
  const [showCalcEditor, setShowCalcEditor] = useState(false);
  // The field's original type before the admin started editing — used to detect
  // type changes so we can warn about / offer to seed options for the new type.
  const [originalType, setOriginalType] = useState<FieldType | null>(null);
  const [derivingOptions, setDerivingOptions] = useState(false);

  const needsOptions = (type: FieldType) =>
    type === 'select' || type === 'multi_select' || type === 'checkbox' || type === 'radio';

  // TRUS-100: record-type scope for the field being created/edited. null/empty
  // on the form = inherit, which we surface as both Members + Contacts checked.
  const effectiveScope: RecordTypeScope[] =
    formData.applies_to_record_types && formData.applies_to_record_types.length > 0
      ? formData.applies_to_record_types
      : ['member', 'contact'];

  const toggleScope = (rt: RecordTypeScope) => {
    setFormData((prev) => {
      const current: RecordTypeScope[] =
        prev.applies_to_record_types && prev.applies_to_record_types.length > 0
          ? prev.applies_to_record_types
          : ['member', 'contact'];
      const next = current.includes(rt)
        ? current.filter((x) => x !== rt)
        : [...current, rt];
      return { ...prev, applies_to_record_types: next };
    });
  };

  const OPERATORS: { value: VisibilityOperator; label: string }[] = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does Not Equal' },
    { value: 'is_populated', label: 'Is Populated' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'contains', label: 'Contains' },
    { value: 'in', label: 'Is One Of (comma-separated)' },
  ];

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'str',
      friendly_name: '',
      category: '',
      default_value: '',
      mandatory: false,
      str_len: 128,
      nullable: true,
      hidden: false,
      field_options: [],
      visibility_rules: null,
      applies_to_record_types: null,
      computed_formula: null,
    });
    setShowRulesEditor(false);
    setShowCalcEditor(false);
    setShowAddForm(false);
    setEditingField(null);
    setOriginalType(null);
    setDerivingOptions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clean up the payload: strip empty options, omit field_options for non-select types
    const payload = { ...formData };
    if (needsOptions(payload.type)) {
      // Filter out options with empty value or label
      payload.field_options = (payload.field_options || []).filter(
        (opt) => opt.value.trim() && opt.label.trim()
      );
      if (payload.field_options.length === 0) {
        alert('Please add at least one option with a value and label.');
        return;
      }
    } else {
      delete payload.field_options;
    }

    // Normalize record-type scope (TRUS-100 / TRUS-399). Only the exact default
    // — Members + Contacts and nothing else, or an empty selection — folds to
    // null (inherit); every other combination is persisted verbatim so scopes
    // added on top of the defaults are not silently dropped on save.
    payload.applies_to_record_types = normalizeAppliesToScope(
      payload.applies_to_record_types
    );

    // Clean up visibility rules: strip empty conditions, set null if disabled
    if (showRulesEditor && payload.visibility_rules) {
      const validConditions = payload.visibility_rules.conditions.filter(
        (c) => c.field.trim() !== ''
      );
      if (validConditions.length > 0) {
        payload.visibility_rules = { ...payload.visibility_rules, conditions: validConditions };
      } else {
        payload.visibility_rules = null;
      }
    } else {
      payload.visibility_rules = null;
    }

    // TRUS-388: the calculation rule. Closing the section makes the field
    // manually entered again, which needs an EXPLICIT null (not an omitted key)
    // so the backend clears the stored rule instead of leaving it in place.
    if (showCalcEditor && payload.computed_formula?.fn) {
      const rule = { ...payload.computed_formula };
      if (rule.scope === 'cross_record') {
        if (!rule.source) {
          alert('Choose the field to roll up across the household.');
          return;
        }
        delete rule.inputs;
        rule.across = rule.across || 'household_people';
      } else {
        const inputs = (rule.inputs || []).filter((i) => String(i).trim() !== '');
        if (inputs.length === 0) {
          alert('Choose the field this calculation reads.');
          return;
        }
        rule.inputs = inputs;
        delete rule.source;
        delete rule.across;
      }
      if (rule.fn !== 'date_diff') delete rule.unit;
      payload.computed_formula = rule;
    } else {
      payload.computed_formula = null;
    }

    if (editingField) {
      // Name is immutable; type CAN be changed (TRUS-59 #1). Only send `type`
      // when it actually changed so we don't trigger a no-op type rewrite.
      const { name, type, ...rest } = payload;
      const updateData: Partial<Omit<CustomFieldCreate, 'name'>> =
        originalType && type !== originalType ? { ...rest, type } : rest;
      const success = await updateField(editingField, updateData);
      if (success) {
        resetForm();
      }
    } else {
      // Create new field
      const success = await createField(payload);
      if (success) {
        resetForm();
      }
    }
  };

  /**
   * When converting a custom field to a select-like type, pre-populate the
   * option list from the distinct values currently stored in the DB so admins
   * don't lose data on save. Merged with whatever options already exist on the
   * form (deduped by value) so re-clicking is idempotent.
   */
  const handleDeriveOptions = async () => {
    if (!editingField) return;
    setDerivingOptions(true);
    try {
      const distinct = await membersAPI.getCustomFieldDistinctValues(editingField);
      if (distinct.length === 0) {
        alert('No existing values found for this field — add options manually.');
        return;
      }
      const existing = formData.field_options || [];
      const existingValues = new Set(existing.map((o) => o.value));
      const merged = [
        ...existing,
        ...distinct.filter((o) => !existingValues.has(o.value)),
      ];
      setFormData({ ...formData, field_options: merged });
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to fetch existing values.');
    } finally {
      setDerivingOptions(false);
    }
  };

  const handleEdit = (fieldId: number) => {
    const field = fields.find((f) => f.id === fieldId);
    if (field) {
      setFormData({
        name: field.name,
        type: field.type,
        friendly_name: field.friendly_name,
        // TRUS-372: normalize a key-form or display-name category to the
        // structured category's display_name so the Category <Select> (keyed by
        // display_name) reflects the field's real category instead of blanking.
        category: resolveCategoryDisplayName(field.category, fieldCategories),
        default_value: field.default_value || '',
        mandatory: field.mandatory,
        str_len: field.str_len || 128,
        nullable: field.nullable,
        hidden: field.hidden,
        field_options: field.field_options || [],
        visibility_rules: field.visibility_rules || null,
        applies_to_record_types: field.applies_to_record_types ?? null,
        computed_formula: field.computed_formula ?? null,
      });
      setShowRulesEditor(!!field.visibility_rules);
      setShowCalcEditor(!!field.computed_formula?.fn);
      setEditingField(fieldId);
      setOriginalType(field.type);
      setShowAddForm(true);
    }
  };

  const handleDelete = async (fieldId: number, fieldName: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${fieldName}"? This will also delete all data stored in this field for all members.`
      )
    ) {
      await deleteField(fieldId);
    }
  };

  const handleNavigateToIntegrations = () => {
    if (onNavigate) {
      onNavigate('integrations');
    }
  };

  // TRUS-394: one row shape for both origins so the table body renders them
  // identically and the filters/grouping below don't have to branch.
  type BuilderRow = {
    /** React key — origins have independent id spaces, so namespace them. */
    key: string;
    origin: 'standard' | 'custom';
    displayName: string;
    /** Internal name: field_key for standard, com_fields.name for custom. */
    internalName: string;
    type: string;
    /** Canonical category display name, or null when uncategorized. */
    category: string | null;
    required: boolean;
    hidden: boolean;
    /** Populated for custom rows only; drives Edit/Delete and the extra detail
     *  cells (options count, scope badge, visibility rules). */
    custom?: (typeof fields)[number];
    standard?: StandardFieldRow;
  };

  const standardRows: BuilderRow[] = (standardFields ?? []).map((sf) => ({
    key: `standard-${sf.field_key}`,
    origin: 'standard',
    displayName: sf.display_name,
    internalName: sf.field_key,
    type: sf.field_type,
    category: sf.category ? resolveCategoryDisplayName(sf.category, fieldCategories) : null,
    required: sf.is_required,
    hidden: sf.is_hidden,
    standard: sf,
  }));

  const customRows: BuilderRow[] = fields.map((f) => {
    // A FieldConfiguration category overrides the field's native
    // com_fields.category — that's what Profile Builder groups by, so show it.
    const rawCategory = customCategoryOverrides?.[f.id] ?? f.category;
    return {
      key: `custom-${f.id}`,
      origin: 'custom' as const,
      displayName: f.friendly_name,
      internalName: f.name,
      type: f.type,
      category: rawCategory ? resolveCategoryDisplayName(rawCategory, fieldCategories) : null,
      required: !!f.mandatory,
      hidden: !!f.hidden,
      custom: f,
    };
  });

  // One category rule for both origins, applied to the row's RESOLVED category.
  // TRUS-372: row.category is already canonicalised to the display name, so a
  // category stored in key form and in display form filter together. Using the
  // resolved value also means a custom field whose FieldConfiguration overrides
  // its native category filters under the category it actually renders in.
  const categoryMatches = (row: BuilderRow) =>
    selectedCategory === 'all' ||
    (selectedCategory === '__uncategorized__'
      ? !row.category
      : row.category === selectedCategory);

  const q = search.trim().toLowerCase();
  const builderRows: BuilderRow[] = [...standardRows, ...customRows]
    .filter((row) => originFilter === 'all' || row.origin === originFilter)
    .filter(categoryMatches)
    .filter(
      (row) =>
        !q ||
        row.displayName.toLowerCase().includes(q) ||
        row.internalName.toLowerCase().includes(q)
    );

  const UNCATEGORIZED_GROUP = t('admin.uncategorized', 'Uncategorized');

  /** Rows bucketed by category, in alphabetical order with Uncategorized last —
   *  the browsing the retired Field Display tab offered for standard fields. */
  const groupedRows: [string, BuilderRow[]][] = (() => {
    const buckets = new Map<string, BuilderRow[]>();
    builderRows.forEach((row) => {
      const name = row.category || UNCATEGORIZED_GROUP;
      const bucket = buckets.get(name);
      if (bucket) bucket.push(row);
      else buckets.set(name, [row]);
    });
    return Array.from(buckets.entries()).sort(([a], [b]) => {
      if (a === UNCATEGORIZED_GROUP) return 1;
      if (b === UNCATEGORIZED_GROUP) return -1;
      return a.localeCompare(b);
    });
  })();

  const toggleGroup = (name: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // --- Bulk category assignment (TRUS-394 item 6) -------------------------
  // Selection is deliberately scoped to the rows currently on screen: a filter
  // or search change can drop a selected row, and silently rewriting a field
  // the admin can no longer see would be the wrong kind of surprise.
  const visibleRowKeys = builderRows.map((r) => r.key);
  const selectedVisibleKeys = visibleRowKeys.filter((k) => selectedRowKeys.has(k));
  const allVisibleSelected =
    visibleRowKeys.length > 0 && selectedVisibleKeys.length === visibleRowKeys.length;

  const toggleRowSelected = (key: string) => {
    setBulkResult(null);
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setBulkResult(null);
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleRowKeys.forEach((k) => next.delete(k));
      else visibleRowKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkResult(null);
  };

  // Bulk assign needs somewhere to assign TO. With no structured categories the
  // selection column would be a control that can't do anything, so hide it.
  const canBulkAssign = !!assignableCategories && assignableCategories.length > 0;

  const applyBulkCategory = async () => {
    if (!bulkCategoryKey || selectedVisibleKeys.length === 0) return;

    const byKey = new Map(builderRows.map((r) => [r.key, r]));
    // Send only field identity + the category. Every other column on
    // BulkFieldConfigUpdate is Optional and COALESCEd server-side, so omitting
    // them preserves each field's saved display name, hidden flag and order.
    const updates = selectedVisibleKeys
      .map((k) => byKey.get(k))
      .filter((r): r is BuilderRow => !!r)
      .map((r) => ({
        field_key: r.internalName,
        is_core_field: r.origin === 'standard',
        category_key: bulkCategoryKey,
      }));

    setBulkApplying(true);
    setBulkResult(null);
    try {
      const res = await membersAPI.bulkUpdateFieldConfigurations(updates);
      const applied = (res.updated ?? 0) + (res.created ?? 0);
      const label =
        assignableCategories?.find((c) => c.category_key === bulkCategoryKey)?.display_name ??
        bulkCategoryKey;
      setBulkResult(
        res.errors && res.errors.length > 0
          ? `Assigned ${applied} of ${updates.length} to "${label}". ${res.errors.length} failed: ${res.errors[0]}`
          : `Assigned ${applied} ${applied === 1 ? 'field' : 'fields'} to "${label}".`
      );
      if (!res.errors || res.errors.length === 0) {
        setSelectedRowKeys(new Set());
        setBulkCategoryKey('');
      }
      // The category override lives on the configuration row, which this
      // component doesn't own — refetch both sides or the table keeps showing
      // the old category and the write looks like it failed.
      await refetch();
      onBulkApplied?.();
    } catch (e) {
      setBulkResult(
        `Could not assign the category: ${e instanceof Error ? e.message : 'unknown error'}`
      );
    } finally {
      setBulkApplying(false);
    }
  };

  /**
   * One table row, standard or custom. Both origins get the same columns and the
   * same Edit affordance; the differences are only what the platform actually
   * allows — a standard field has no definition to delete and system fields
   * can't be hidden or renamed at all.
   */
  const renderRow = (row: BuilderRow) => {
    const isStandard = row.origin === 'standard';
    const locked = isStandard && !!row.standard?.is_system_field;
    const field = row.custom;

    return (
      <TableRow key={row.key} className={row.hidden ? 'opacity-60' : ''}>
        {canBulkAssign && (
          <TableCell className="w-8">
            <Checkbox
              checked={selectedRowKeys.has(row.key)}
              onCheckedChange={() => toggleRowSelected(row.key)}
              aria-label={`Select ${row.displayName}`}
            />
          </TableCell>
        )}
        <TableCell className="text-gray-900 dark:text-gray-100">
          <div className="flex flex-col gap-1">
            <span>
              {row.displayName}
              {/* Surface renames of standard fields, as Field Display did — the
                  platform default is what other docs and support will call it. */}
              {row.standard && row.standard.display_name !== row.standard.default_display_name && (
                <span className="text-muted-foreground text-xs ml-2">
                  (was: {row.standard.default_display_name})
                </span>
              )}
            </span>
            {/* Record-type scope badge (TRUS-100) — always shown for custom
                fields so users can see/trust which records a field applies to.
                Standard fields exist on every record type, so the badge would
                be noise; they get a System marker instead where it applies. */}
            {field ? (
              <Badge variant="outline" size="sm" className="w-fit gap-1">
                {recordTypeScopeLabel(field.applies_to_record_types)}
              </Badge>
            ) : (
              locked && (
                <Badge variant="outline" size="sm" className="w-fit">
                  {t('admin.system', 'System')}
                </Badge>
              )
            )}
          </div>
        </TableCell>
        <TableCell>
          <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-800 dark:text-gray-200">
            {row.internalName}
          </code>
        </TableCell>
        <TableCell>
          <Badge
            variant={isStandard ? 'secondary' : 'outline'}
            size="sm"
            title={
              isStandard
                ? t('admin.origin_standard_hint', 'Built into the platform — configure it, but it can\'t be deleted.')
                : t('admin.origin_custom_hint', 'Created by your organization.')
            }
          >
            {isStandard ? t('admin.origin_standard', 'Standard') : t('admin.origin_custom', 'Custom')}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant="primary" size="sm" title={getFieldTypeDescription(row.type)}>
            {getFieldTypeLabel(row.type)}
          </Badge>
          {field?.field_options && field.field_options.length > 0 && (
            <span className="ml-1 text-xs text-gray-500">
              ({field.field_options.length} options)
            </span>
          )}
        </TableCell>
        <TableCell className="text-gray-900 dark:text-gray-100">
          {row.category || '-'}
        </TableCell>
        <TableCell className="text-gray-900 dark:text-gray-100">
          {row.required ? 'Yes' : 'No'}
        </TableCell>
        <TableCell className="text-gray-900 dark:text-gray-100">
          {/* Standard fields keep the one-click visibility toggle the retired
              Field Display tab had. Custom fields have no equivalent inline
              save path here, so they report state and edit through the form. */}
          {isStandard && onToggleStandardVisibility ? (
            <Switch
              checked={!row.hidden}
              onCheckedChange={() => onToggleStandardVisibility(row.internalName)}
              disabled={locked}
              aria-label={t('admin.visible', 'Visible')}
            />
          ) : (
            <>
              {row.hidden ? 'Yes' : 'No'}
              {field?.visibility_rules && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">{t('fieldConfig.dependsOn', 'Depends on:')} </span>
                  {field.visibility_rules.conditions.map((c, i) => {
                    const depField = fields.find((f) => f.name === c.field);
                    const label = depField ? depField.friendly_name : c.field;
                    return (
                      <span key={i}>
                        {i > 0 && (
                          <span className="text-gray-400 mx-1">
                            {field.visibility_rules!.logic === 'AND' ? '&' : 'or'}
                          </span>
                        )}
                        <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
                        {' '}{c.operator.replace('_', ' ')}
                        {c.value && <> &ldquo;{c.value}&rdquo;</>}
                      </span>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              icon={<Pencil className="w-4 h-4" />}
              disabled={locked}
              title={
                locked
                  ? t('admin.system_field_not_editable', 'System fields can\'t be changed.')
                  : undefined
              }
              onClick={() =>
                field ? handleEdit(field.id) : onEditStandardField?.(row.internalName)
              }
            >
              {t('admin.edit', 'Edit')}
            </Button>
            {field && (
              <Button
                size="sm"
                variant="outline"
                icon={<Trash2 className="w-4 h-4" />}
                onClick={() => handleDelete(field.id, field.friendly_name)}
                className="border-red-600 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                {t('admin.delete', 'Delete')}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  // TRUS-372: collapse the filter list to canonical display names so a category
  // stored in both forms (e.g. "address" and "Address") shows once, not twice.
  // Standard fields carry categories of their own (a configured category name or
  // their core field_group), so fold those in too or filtering to a
  // standard-only category would be impossible.
  const filterCategoryOptions = Array.from(
    new Map(
      [...categories, ...(standardFields ?? []).map((sf) => sf.category)]
        .filter((cat): cat is string => !!cat && typeof cat === 'string' && cat.trim() !== '')
        .map((cat) => {
          const canonical = resolveCategoryDisplayName(cat, fieldCategories);
          return [canonical.toLowerCase(), canonical] as const;
        })
    ).values()
  ).sort((a, b) => a.localeCompare(b));

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
            <Sliders className="h-6 w-6 text-blue-600" />
            {standardFields
              ? t('admin.field_builder', 'Field Builder')
              : t('admin.custom_field_configuration', 'Custom Field Configuration')}
          </h1>
          <p className="text-base text-gray-600 dark:text-gray-400">
            {standardFields
              ? t(
                  'admin.manage_standard_and_custom_field_definitions',
                  "Manage your organization's standard and custom field definitions in one place."
                )
              : t('admin.manage_organizations_custom_field_definitions', 'Manage organization\'s custom field definitions')}
          </p>
        </div>
        {!showAddForm && (
          <div className="flex gap-2">
            {onNavigate && (
              <Button
                variant="outline"
                icon={<ArrowRight className="w-4 h-4" />}
                onClick={handleNavigateToIntegrations}
              >
                {t('admin.go_to_integration_workbench', 'Go to Integration Workbench')}
              </Button>
            )}
            <Button
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddForm(true)}
            >
              {t('admin.add_field', 'Add Field')}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <ApiErrorAlert
          error={error}
          action={errorAction}
          retryable={retryable}
          onRetry={refetch}
          isRetrying={loading}
          className="mb-6"
        />
      )}

      {/* Add/Edit Form — Full Page View (replaces the list) */}
      {showAddForm ? (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-1">
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {editingField ? 'Edit Field' : 'Add New Field'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {editingField
                    ? 'Update field properties. The internal name is immutable, but the field type can be changed — existing values stay in place as text.'
                    : 'Define a new custom field for your organization. Fields store additional member data.'}
                </p>
              </div>
            </div>
            <Separator className="my-4" />
            <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="field-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Name (internal)
                  </Label>
                  <Input
                    id="field-name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value.toLowerCase() })
                    }
                    placeholder="e.g., ecm_authorization_number"
                    required
                    disabled={!!editingField}
                    pattern="[a-z0-9_]+"
                    title="Lowercase letters, numbers, and underscores only"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Use lowercase letters, numbers, and underscores only
                  </p>
                </div>

                <div>
                  <Label htmlFor="display-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.display_name_1', 'Display Name')}
                  </Label>
                  <Input
                    id="display-name"
                    value={formData.friendly_name}
                    onChange={(e) => setFormData({ ...formData, friendly_name: e.target.value })}
                    placeholder="e.g., ECM Authorization Number"
                    required
                    className="w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="field-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.field_type', 'Field Type')}
                  </Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value as FieldType })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('admin.field_type_1', 'Field Type')}>
                        {getFieldTypeLabel(formData.type)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {FIELD_TYPE_DESCRIPTIONS[type].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.type && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {getFieldTypeDescription(formData.type)}
                    </p>
                  )}
                  {editingField && originalType && formData.type !== originalType && (
                    <div className="mt-2 rounded border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 p-2 text-xs text-amber-800 dark:text-amber-200">
                      <strong>{t('fieldConfig.changingTypeFromTo', 'Changing type from {{from}} to {{to}}.', { from: getFieldTypeLabel(originalType), to: getFieldTypeLabel(formData.type) })}</strong>
                      {' '}{t('fieldConfig.typeChangeValuesNote', 'Values are stored as text, so nothing is deleted — but they may render or validate differently under the new type.')}
                      {needsOptions(formData.type) && !needsOptions(originalType) && (
                        <> {t('fieldConfig.typeChangeDeriveHint', 'Use Derive from existing data below to pre-populate options from current values.')}</>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.category_1', 'Category')}
                  </Label>
                  <Select
                    value={formData.category || '__none__'}
                    onValueChange={(value) => setFormData({ ...formData, category: value === '__none__' ? '' : value })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('admin.select_category', 'Select a category')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('admin.no_category', 'No Category')}</SelectItem>
                      {fieldCategories
                        .filter((cat) => cat.is_active)
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((cat) => (
                          <SelectItem key={cat.id} value={cat.display_name}>
                            {cat.icon && <span className="mr-2">{cat.icon}</span>}
                            {cat.display_name}
                          </SelectItem>
                        ))}
                      {categories
                        .filter((cat) => cat && !fieldCategories.some((fc) =>
                          fc.display_name.toLowerCase() === cat.toLowerCase() ||
                          fc.category_key === cat.toLowerCase().replace(/\s+/g, '_')
                        ))
                        .map((cat) => (
                          <SelectItem key={`legacy_${cat}`} value={cat}>
                            {cat} <span className="text-muted-foreground text-xs">{t('fieldConfig.notSynced', '(not synced)')}</span>
                          </SelectItem>
                        ))}
                      {formData.category &&
                       !fieldCategories.some((fc) => fc.display_name === formData.category) &&
                       !categories.includes(formData.category) && (
                        <SelectItem key={`current_${formData.category}`} value={formData.category}>
                          {formData.category} <span className="text-muted-foreground text-xs">{t('fieldConfig.current', '(current)')}</span>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('admin.category_hint', 'Categories are managed in Member Field Configuration page')}
                  </p>
                </div>
              </div>

              {/* Applies to — which record type(s) this field is placed on. Each option
                  has a real value store and surface behind it; an empty selection
                  inherits the field's default, which is members + contacts. */}
              <div>
                <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.applies_to', 'Applies to')}
                </Label>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  {RECORD_TYPE_SCOPES.filter((sc) => sc.assignable).map((sc) => (
                    <label
                      key={sc.value}
                      title={sc.hint}
                      className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300"
                    >
                      <Checkbox
                        checked={effectiveScope.includes(sc.value)}
                        onCheckedChange={() => toggleScope(sc.value)}
                      />
                      {sc.label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t(
                    'admin.applies_to_hint',
                    "Which record types this field appears on. Nothing selected follows the field's default — members and contacts."
                  )}
                </p>
                {/* TRUS-402: a field inside a repeatable group renders as a column of
                    that group, so its placement comes from the GROUP's record types —
                    this picker cannot move it. Saying so is the point: the save used to
                    succeed, persist, and change nothing on the record, with no error to
                    reveal it, so the only way to find out was to go and look. */}
                {formData.field_group_id != null && (
                  <p className="text-xs mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                    This field belongs to a field group, so it appears wherever the{' '}
                    <strong>group</strong> appears — this setting will not move it. To place it
                    on Households or Family Members, set “Appears on” for the group itself in{' '}
                    <strong>Field Groups</strong>.
                  </p>
                )}
              </div>

              {formData.type === 'str' && (
                <div className="max-w-xs">
                  <Label htmlFor="max-length" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.max_length', 'Max Length')}
                  </Label>
                  <Input
                    id="max-length"
                    type="number"
                    value={formData.str_len}
                    onChange={(e) =>
                      setFormData({ ...formData, str_len: parseInt(e.target.value) || 128 })
                    }
                    min={1}
                    max={65535}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Maximum characters per entry (up to 65,535 for long narratives).
                  </p>
                </div>
              )}

              {needsOptions(formData.type) && (
                <div>
                  <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.field_options', 'Options')} <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {formData.type === 'select'
                      ? 'Users will pick one value from this list.'
                      : formData.type === 'multi_select'
                      ? 'Users can pick one or more values from this list.'
                      : 'Each option will render as a checkbox.'}
                  </p>
                  <div className="space-y-2">
                    {(formData.field_options || []).map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          placeholder="Value"
                          value={opt.value}
                          onChange={(e) => {
                            const updated = [...(formData.field_options || [])];
                            updated[idx] = { ...updated[idx], value: e.target.value };
                            setFormData({ ...formData, field_options: updated });
                          }}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Display Label"
                          value={opt.label}
                          onChange={(e) => {
                            const updated = [...(formData.field_options || [])];
                            updated[idx] = { ...updated[idx], label: e.target.value };
                            setFormData({ ...formData, field_options: updated });
                          }}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updated = (formData.field_options || []).filter((_, i) => i !== idx);
                            setFormData({ ...formData, field_options: updated });
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const updated = [...(formData.field_options || []), { value: '', label: '' }];
                          setFormData({ ...formData, field_options: updated });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add Option
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={(formData.field_options || []).length < 2}
                        onClick={() => {
                          const sorted = [...(formData.field_options || [])].sort((a, b) =>
                            a.label.localeCompare(b.label)
                          );
                          setFormData({ ...formData, field_options: sorted });
                        }}
                        title="Sort options alphabetically by display label"
                      >
                        Sort A–Z
                      </Button>
                      {editingField && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleDeriveOptions}
                          disabled={derivingOptions}
                          title="Add the distinct values currently stored for this field as options"
                        >
                          {derivingOptions ? 'Loading…' : 'Derive from existing data'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="max-w-md">
                <Label htmlFor="default-value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.default_value', 'Default Value')}
                </Label>
                <Input
                  id="default-value"
                  value={formData.default_value}
                  onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="mandatory"
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <Checkbox
                    id="mandatory"
                    checked={formData.mandatory}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, mandatory: checked === true })
                    }
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Mandatory
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Field must be filled in for all records
                    </p>
                  </div>
                </label>

                <label
                  htmlFor="not-nullable"
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <Checkbox
                    id="not-nullable"
                    checked={!formData.nullable}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, nullable: checked !== true })
                    }
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('admin.not_nullable', 'Not Nullable')}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Field cannot be empty or null in the database
                    </p>
                  </div>
                </label>

                <label
                  htmlFor="hidden"
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <Checkbox
                    id="hidden"
                    checked={formData.hidden}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, hidden: checked === true })
                    }
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Hidden
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      For internal system use only, not shown to users
                    </p>
                  </div>
                </label>
              </div>

              {/* Calculation rule (TRUS-388). Sits alongside Conditional
                  Visibility because it answers the sibling question: that one
                  controls WHEN a field appears, this one controls WHERE ITS
                  VALUE COMES FROM. A field with a rule is calculated by the
                  system and read-only to users. */}
              <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Calculation
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!showCalcEditor) {
                        setShowCalcEditor(true);
                        if (!formData.computed_formula?.fn) {
                          setFormData({
                            ...formData,
                            computed_formula: {
                              fn: 'date_diff',
                              scope: 'same_record',
                              inputs: [],
                              unit: 'years',
                            },
                          });
                        }
                      } else {
                        setShowCalcEditor(false);
                        setFormData({ ...formData, computed_formula: null });
                      }
                    }}
                  >
                    {showCalcEditor ? 'Entered manually' : 'Calculate this field'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {showCalcEditor
                    ? 'The system fills this field in and keeps it up to date. Users see it but cannot edit it, and no typed value is ever stored.'
                    : 'Users type this field in themselves. Click "Calculate this field" to have the system derive it — an age from a date of birth, or a total across everyone in a household.'}
                </p>

                {showCalcEditor && formData.computed_formula && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400 w-20">Calculate</span>
                      <Select
                        value={String(formData.computed_formula.fn || 'date_diff')}
                        onValueChange={(val) =>
                          setFormData({
                            ...formData,
                            computed_formula: {
                              ...formData.computed_formula!,
                              fn: val,
                              // A date difference is only meaningful on the same
                              // record; a roll-up only across linked records.
                              scope: val === 'date_diff' ? 'same_record' : formData.computed_formula!.scope,
                              unit: val === 'date_diff' ? formData.computed_formula!.unit || 'years' : undefined,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="w-72 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(CALC_TYPE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.computed_formula.fn === 'date_diff' ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400 w-20">Measured in</span>
                        <Select
                          value={String(formData.computed_formula.unit || 'years')}
                          onValueChange={(val) =>
                            setFormData({
                              ...formData,
                              computed_formula: { ...formData.computed_formula!, unit: val as 'years' | 'months' | 'days' },
                            })
                          }
                        >
                          <SelectTrigger className="w-40 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(UNIT_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400 w-20">Across</span>
                        <Select
                          value={
                            formData.computed_formula.scope === 'cross_record'
                              ? String(formData.computed_formula.across || 'household_people')
                              : 'same_record'
                          }
                          onValueChange={(val) =>
                            setFormData({
                              ...formData,
                              computed_formula:
                                val === 'same_record'
                                  ? { ...formData.computed_formula!, scope: 'same_record', across: undefined }
                                  : {
                                      ...formData.computed_formula!,
                                      scope: 'cross_record',
                                      across: val as 'household_members' | 'household_family_members' | 'household_people',
                                    },
                            })
                          }
                        >
                          <SelectTrigger className="w-96 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="same_record">Other fields on this same record</SelectItem>
                            {Object.entries(ACROSS_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="flex items-start gap-2 flex-wrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400 w-20 pt-1.5">
                        {formData.computed_formula.scope === 'cross_record' ? 'Roll up' : 'From'}
                      </span>
                      <Select
                        value={
                          formData.computed_formula.scope === 'cross_record'
                            ? String(formData.computed_formula.source || '__select__')
                            : String(formData.computed_formula.inputs?.[0] ?? '__select__')
                        }
                        onValueChange={(val) => {
                          const picked = val === '__select__' ? '' : val;
                          setFormData({
                            ...formData,
                            computed_formula:
                              formData.computed_formula!.scope === 'cross_record'
                                ? { ...formData.computed_formula!, source: picked }
                                : { ...formData.computed_formula!, inputs: picked ? [picked] : [] },
                          });
                        }}
                      >
                        <SelectTrigger className="w-72 h-8">
                          <SelectValue placeholder="Select field..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__select__" disabled>
                            {t('fieldConfig.selectFieldPlaceholder', 'Select field...')}
                          </SelectItem>
                          {/* A date difference needs a date source and a sum a
                              numeric one — the backend refuses anything else, so
                              filtering here means the admin never gets to submit
                              a rule that will be rejected. `dob` is offered
                              explicitly because it is a CORE column, not a
                              custom field, and it is Age's usual source. */}
                          {formData.computed_formula.fn === 'date_diff' && (
                            <SelectItem value="dob">Date of Birth (core field)</SelectItem>
                          )}
                          {fields
                            .filter((f) => f.name !== formData.name)
                            .filter((f) =>
                              formData.computed_formula!.fn === 'date_diff'
                                ? ['date', 'datetime', 'fixed_date'].includes(f.type)
                                : formData.computed_formula!.fn === 'sum'
                                ? ['int', 'computed'].includes(f.type) || !!f.computed_formula
                                : true,
                            )
                            .map((f) => (
                              <SelectItem key={f.name} value={f.name}>
                                {f.friendly_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                      {describeRule(formData.computed_formula)}
                    </p>
                  </div>
                )}
              </div>

              {/* Visibility Rules / Dependencies */}
              <div className={`border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 ${formData.hidden ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Conditional Visibility
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={formData.hidden}
                    onClick={() => {
                      if (!showRulesEditor) {
                        setShowRulesEditor(true);
                        if (!formData.visibility_rules) {
                          setFormData({
                            ...formData,
                            visibility_rules: {
                              conditions: [{ field: '', operator: 'is_populated' as VisibilityOperator, value: '' }],
                              logic: 'AND',
                              when_hidden: 'optional',
                            },
                          });
                        }
                      } else {
                        setShowRulesEditor(false);
                        setFormData({ ...formData, visibility_rules: null });
                      }
                    }}
                  >
                    {showRulesEditor ? 'Always Visible' : 'Add Conditions'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {formData.hidden
                    ? 'Hidden fields are never shown to users — visibility conditions have no effect.'
                    : showRulesEditor
                    ? 'This field will only appear when the conditions below are met.'
                    : 'This field is always visible. Click "Add Conditions" to show it only when other fields have specific values.'}
                </p>

                {showRulesEditor && formData.visibility_rules && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('fieldConfig.showWhen', 'Show when')}</span>
                      <Select
                        value={formData.visibility_rules.logic}
                        onValueChange={(val) =>
                          setFormData({
                            ...formData,
                            visibility_rules: { ...formData.visibility_rules!, logic: val as 'AND' | 'OR' },
                          })
                        }
                      >
                        <SelectTrigger className="w-24 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND">{t('fieldConfig.logicAll', 'ALL')}</SelectItem>
                          <SelectItem value="OR">{t('fieldConfig.logicAny', 'ANY')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-gray-600 dark:text-gray-400">{t('fieldConfig.ofTheseConditionsAreMet', 'of these conditions are met:')}</span>
                    </div>

                    {formData.visibility_rules.conditions.map((cond, idx) => (
                      <div key={idx} className="flex items-center gap-2 flex-wrap">
                        <Select
                          value={cond.field || '__select__'}
                          onValueChange={(val) => {
                            const updated = [...formData.visibility_rules!.conditions];
                            updated[idx] = { ...updated[idx], field: val === '__select__' ? '' : val, value: '' };
                            setFormData({
                              ...formData,
                              visibility_rules: { ...formData.visibility_rules!, conditions: updated },
                            });
                          }}
                        >
                          <SelectTrigger className="w-44 h-8">
                            <SelectValue placeholder="Select field..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__select__" disabled>{t('fieldConfig.selectFieldPlaceholder', 'Select field...')}</SelectItem>
                            {fields
                              .filter((f) => f.name !== formData.name)
                              .map((f) => (
                                <SelectItem key={f.name} value={f.name}>
                                  {f.friendly_name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={cond.operator}
                          onValueChange={(val) => {
                            const updated = [...formData.visibility_rules!.conditions];
                            updated[idx] = { ...updated[idx], operator: val as VisibilityOperator };
                            setFormData({
                              ...formData,
                              visibility_rules: { ...formData.visibility_rules!, conditions: updated },
                            });
                          }}
                        >
                          <SelectTrigger className="w-40 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {OPERATORS.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {cond.operator !== 'is_populated' && cond.operator !== 'is_empty' && (() => {
                          const condField = fields.find((f) => f.name === cond.field);
                          const isMultiValue = cond.operator === 'in';
                          const explicitOptions = condField?.field_options && condField.field_options.length > 0
                            ? [...condField.field_options].sort((a, b) => a.label.localeCompare(b.label))
                            : null;
                          const boolOptions: FieldOption[] | null =
                            condField && (condField.type === 'bool' || condField.type === 'toggle')
                              ? [
                                  { value: 'true', label: 'Yes' },
                                  { value: 'false', label: 'No' },
                                ]
                              : null;
                          const valueOptions = explicitOptions || boolOptions;
                          const updateValue = (val: string) => {
                            const updated = [...formData.visibility_rules!.conditions];
                            updated[idx] = { ...updated[idx], value: val };
                            setFormData({
                              ...formData,
                              visibility_rules: { ...formData.visibility_rules!, conditions: updated },
                            });
                          };

                          if (valueOptions && isMultiValue) {
                            const selected = (cond.value || '')
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean);
                            const toggle = (optValue: string) => {
                              const next = selected.includes(optValue)
                                ? selected.filter((v) => v !== optValue)
                                : [...selected, optValue];
                              updateValue(next.join(','));
                            };
                            return (
                              <div className="flex flex-wrap items-center gap-1 min-w-[10rem]">
                                {valueOptions.map((opt) => {
                                  const active = selected.includes(opt.value);
                                  return (
                                    <Button
                                      key={opt.value}
                                      type="button"
                                      variant={active ? 'primary' : 'outline'}
                                      size="sm"
                                      className="h-8"
                                      onClick={() => toggle(opt.value)}
                                    >
                                      {opt.label}
                                    </Button>
                                  );
                                })}
                              </div>
                            );
                          }

                          if (valueOptions) {
                            return (
                              <Select
                                value={cond.value || '__select__'}
                                onValueChange={(val) => updateValue(val === '__select__' ? '' : val)}
                              >
                                <SelectTrigger className="min-w-[10rem] max-w-[20rem] h-8 w-auto">
                                  <SelectValue placeholder="Select value..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__select__" disabled>{t('fieldConfig.selectValuePlaceholder', 'Select value...')}</SelectItem>
                                  {valueOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          }

                          const inputType =
                            condField?.type === 'int' ? 'number'
                            : condField?.type === 'date' || condField?.type === 'fixed_date' ? 'date'
                            : condField?.type === 'datetime' ? 'datetime-local'
                            : condField?.type === 'time' ? 'time'
                            : 'text';
                          return (
                            <Input
                              type={inputType}
                              placeholder="Value"
                              value={cond.value || ''}
                              onChange={(e) => updateValue(e.target.value)}
                              className="w-40 h-8"
                            />
                          );
                        })()}

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updated = formData.visibility_rules!.conditions.filter((_, i) => i !== idx);
                            setFormData({
                              ...formData,
                              visibility_rules: { ...formData.visibility_rules!, conditions: updated },
                            });
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const updated = [
                          ...formData.visibility_rules!.conditions,
                          { field: '', operator: 'is_populated' as VisibilityOperator, value: '' },
                        ];
                        setFormData({
                          ...formData,
                          visibility_rules: { ...formData.visibility_rules!, conditions: updated },
                        });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Condition
                    </Button>

                    <div className="flex items-center gap-2 text-sm pt-2 border-t">
                      <span className="text-gray-600 dark:text-gray-400">{t('fieldConfig.whenHidden', 'When hidden:')}</span>
                      <Select
                        value={formData.visibility_rules.when_hidden}
                        onValueChange={(val) =>
                          setFormData({
                            ...formData,
                            visibility_rules: {
                              ...formData.visibility_rules!,
                              when_hidden: val as 'optional' | 'skip',
                            },
                          })
                        }
                      >
                        <SelectTrigger className="w-48 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="optional">{t('fieldConfig.preserveExistingValue', 'Preserve existing value')}</SelectItem>
                          <SelectItem value="skip">{t('fieldConfig.skipValidationEntirely', 'Skip validation entirely')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              <Separator />
              <div className="flex justify-end gap-3">
                <Button onClick={resetForm} variant="ghost">
                  {t('admin.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  loading={loading}
                >
                  {editingField ? 'Update Field' : 'Create Field'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <>
      {/* Filters — category (both origins), plus the origin/search/grouping
          controls the merged standard+custom list needs (TRUS-394). */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-64">
            <Label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.filter_by_category', 'Filter by category')}
            </Label>
            <Select
              value={selectedCategory}
              onValueChange={(value) => setSelectedCategory(value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('admin.filter_by_category_1', 'Filter by category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.all_categories', 'All Categories')}</SelectItem>
                {filterCategoryOptions.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
                <SelectItem value="__uncategorized__">{t('admin.uncategorized', 'Uncategorized')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {standardFields && (
            <div>
              <Label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.filter_by_origin', 'Show')}
              </Label>
              <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                {([
                  ['all', t('admin.origin_all', 'All fields')],
                  ['standard', t('admin.origin_standard', 'Standard')],
                  ['custom', t('admin.origin_custom', 'Custom')],
                ] as [OriginFilter, string][]).map(([value, label], i) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setOriginFilter(value)}
                    className={`px-3 py-2 text-sm font-medium ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${
                      originFilter === value
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="w-64">
            <Label htmlFor="field-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.search', 'Search')}
            </Label>
            <Input
              id="field-search"
              placeholder={t('admin.search_fields_placeholder', 'Display or internal name…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700 dark:text-gray-300">
            <Switch checked={groupByCategory} onCheckedChange={setGroupByCategory} />
            {t('admin.group_by_category', 'Group by category')}
          </label>
        </div>
      </Card>

      {/* Bulk actions (TRUS-394 item 6) — appears only once rows are selected,
          so the default view stays as quiet as it was. */}
      {canBulkAssign && selectedVisibleKeys.length > 0 && (
        <Card className="p-4 mb-6 border-indigo-300 dark:border-indigo-700">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {selectedVisibleKeys.length}{' '}
              {selectedVisibleKeys.length === 1 ? 'field' : 'fields'} selected
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('admin.assign_to_category', 'Assign to category')}
            </span>
            <Select value={bulkCategoryKey} onValueChange={setBulkCategoryKey}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder={t('admin.choose_a_category', 'Choose a category…')} />
              </SelectTrigger>
              <SelectContent>
                {assignableCategories!.map((cat) => (
                  <SelectItem key={cat.category_key} value={cat.category_key}>
                    {cat.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="primary"
              disabled={!bulkCategoryKey || bulkApplying}
              loading={bulkApplying}
              onClick={applyBulkCategory}
            >
              {t('admin.apply', 'Apply')}
            </Button>
            <Button variant="ghost" onClick={clearSelection} disabled={bulkApplying}>
              {t('admin.clear_selection', 'Clear selection')}
            </Button>
          </div>
          {bulkResult && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">{bulkResult}</p>
          )}
        </Card>
      )}

      {/* Fields Table */}
      {loading && builderRows.length === 0 ? (
        <div className="w-full mt-8">
          <Progress value={undefined} className="w-full" />
          <p className="text-center text-gray-600 dark:text-gray-400 mt-4">
            {t('admin.loading_fields', 'Loading fields...')}
          </p>
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                {canBulkAssign && (
                  <TableHead className="w-8">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={toggleSelectAllVisible}
                      aria-label={t('admin.select_all_fields', 'Select all shown fields')}
                    />
                  </TableHead>
                )}
                <TableHead>{t('admin.display_name', 'Display Name')}</TableHead>
                <TableHead>{t('admin.internal_name', 'Internal Name')}</TableHead>
                <TableHead>{t('admin.origin', 'Origin')}</TableHead>
                <TableHead>{t('admin.type', 'Type')}</TableHead>
                <TableHead>{t('admin.category', 'Category')}</TableHead>
                <TableHead>{t('admin.required', 'Required')}</TableHead>
                <TableHead>{t('admin.hidden', 'Hidden')}</TableHead>
                <TableHead>{t('admin.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {builderRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canBulkAssign ? 9 : 8} className="text-center">
                    <div className="py-8">
                      <p className="text-base text-gray-600 dark:text-gray-400 mb-2">
                        {t('admin.no_fields_found', 'No fields found')}
                        {recordType && (
                          <>
                            {' '}for the{' '}
                            <span className="font-semibold">
                              {recordType === 'activity_event' ? 'Activity Events' : recordType === 'contact' ? 'Contacts' : 'Members'}
                            </span>
                            {' '}scope.
                          </>
                        )}
                      </p>
                      {recordType === 'member' && (
                        <p className="text-sm text-gray-500 mb-4">
                          {t('fieldConfig.outreachFieldsHint', "If you're looking for outreach / event fields, switch the toggle above to")} <strong>{t('fieldConfig.activityEvents', 'Activity Events')}</strong>.
                        </p>
                      )}
                      <Button
                        variant="primary"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => setShowAddForm(true)}
                      >
                        {t('admin.add_first_field', 'Add First Field')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : groupByCategory ? (
                groupedRows.map(([groupName, rows]) => {
                  const collapsed = collapsedGroups.has(groupName);
                  return (
                    <React.Fragment key={groupName}>
                      <TableRow
                        className="bg-muted/50 hover:bg-muted cursor-pointer"
                        onClick={() => toggleGroup(groupName)}
                      >
                        <TableCell colSpan={canBulkAssign ? 9 : 8} className="font-medium">
                          <span className="inline-flex items-center gap-2">
                            {collapsed ? (
                              <ChevronRight className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                            {groupName}
                            <Badge variant="secondary" size="sm">
                              {rows.length}
                            </Badge>
                          </span>
                        </TableCell>
                      </TableRow>
                      {!collapsed && rows.map(renderRow)}
                    </React.Fragment>
                  );
                })
              ) : (
                builderRows.map(renderRow)
              )}
            </TableBody>
          </Table>
          {builderRows.length > 0 && (
            <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 border-t border-border">
              {builderRows.length} {builderRows.length === 1 ? 'field' : 'fields'}
              {standardFields && (
                <>
                  {' — '}
                  {builderRows.filter((r) => r.origin === 'standard').length} standard,{' '}
                  {builderRows.filter((r) => r.origin === 'custom').length} custom
                </>
              )}
            </div>
          )}
        </Card>
      )}
        </>
      )}
    </div>
  );
}
